import React, { useEffect, useState, useCallback } from 'react';
import { configApi, FeeConfigItem } from '../../../api/config';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../hooks/useToast';
import { CreditFeeCover } from './CreditFeeCover';

interface FxConfig {
  id: string;
  baseNgnUsdRate: number;
  markupPercent: number;
  cardCreationFeeNgn: number;
  cardCreationFeeUsdCents: number;
  cardPhysicalCreationFeeNgn: number;
  businessCardCreationFeeNgn?: number;
}

type EditingField =
  | 'transferMarkup'
  | 'intraBankMarkup'
  | 'fxRate'
  | 'fxMarkup'
  | 'cardNgn'
  | 'cardUsd'
  | 'cardPhysical'
  | 'cardBusiness'
  | 'shopOrder'
  | 'shopCancellation'
  | 'payoutPercent'
  | 'payoutCap'
  | 'airtimePercent'
  | 'dataPercent'
  | 'cablePercent'
  | 'electricityPercent'
  | 'freeConsumer'
  | 'freeBusiness'
  | 'whatsappReminder'
  | null;

type PendingType =
  | 'transfer'
  | 'intraBankTransfer'
  | 'fx'
  | 'cardNgn'
  | 'cardUsd'
  | 'cardPhysical'
  | 'cardBusiness'
  | 'shopOrder'
  | 'shopCancellation'
  | 'payoutPercent'
  | 'payoutCap'
  | 'airtimePercent'
  | 'dataPercent'
  | 'cablePercent'
  | 'electricityPercent'
  | 'freeConsumer'
  | 'freeBusiness'
  | 'whatsappReminder'
  | 'discos'
  | 'enableCommission'
  | null;

/** Electricity companies with their own rate on Safe Haven's sheet — the
 *  same keys the backend uses (`vas_commission_electricity_<key>`). */
const DISCOS: { key: string; label: string }[] = [
  { key: 'aedc_prepaid', label: 'AEDC prepaid' },
  { key: 'aedc_postpaid', label: 'AEDC postpaid' },
  { key: 'ekedc', label: 'EKEDC (Eko)' },
  { key: 'ikedc', label: 'IKEDC (Ikeja)' },
  { key: 'ibedc', label: 'IBEDC (Ibadan)' },
  { key: 'eedc', label: 'EEDC (Enugu)' },
  { key: 'phedc', label: 'PHEDC (Port Harcourt)' },
  { key: 'jedc', label: 'JEDC (Jos)' },
  { key: 'kedco', label: 'KEDCO (Kano)' },
  { key: 'bedc', label: 'BEDC (Benin)' },
  { key: 'kaedco', label: 'KAEDCO (Kaduna)' },
];

const SectionHeader: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
    <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>{title}</h3>
    <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>{description}</p>
  </div>
);

const FieldRow: React.FC<{
  label: string;
  description: string;
  displayValue: string;
  field: string;
  editingField: EditingField;
  editValue: string;
  onEditChange: (val: string) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  canEdit: boolean;
  index: number;
}> = ({
  label,
  description,
  displayValue,
  field,
  editingField,
  editValue,
  onEditChange,
  onEdit,
  onSave,
  onCancel,
  canEdit,
  index,
}) => {
  const isEditing = editingField === field;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(min(100%, 220px), 1fr) auto auto',
        gap: 16,
        padding: '16px 20px',
        alignItems: 'center',
        background: index % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--neutral-800)' }}>
          {label}
        </div>
        <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 }}>
          {description}
        </div>
      </div>

      {isEditing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="number"
            value={editValue}
            onChange={(e) => onEditChange(e.target.value)}
            autoFocus
            style={{
              width: 120,
              height: 36,
              padding: '0 10px',
              fontSize: 14,
              fontFamily: 'var(--font-family)',
              color: 'var(--neutral-800)',
              background: 'var(--neutral-100)',
              border: '1px solid var(--blue-600)',
              borderRadius: 'var(--radius-md)',
              outline: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave();
              if (e.key === 'Escape') onCancel();
            }}
          />
          <Button size="sm" onClick={onSave}>Save</Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      ) : (
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)', fontFamily: 'monospace' }}>
          {displayValue}
        </span>
      )}

      {canEdit && !isEditing && (
        <Button size="sm" variant="secondary" onClick={onEdit}>Edit</Button>
      )}
    </div>
  );
};

export const FeeConfig: React.FC = () => {
  const { admin } = useAuth();
  const toast = useToast();

  const [fees, setFees] = useState<FeeConfigItem[]>([]);
  const [fxConfig, setFxConfig] = useState<FxConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingType, setPendingType] = useState<PendingType>(null);
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [editValues, setEditValues] = useState({
    transferMarkup: '',
    intraBankMarkup: '',
    fxMarkup: '',
    cardNgn: '',
    cardUsd: '',
    cardPhysical: '',
    cardBusiness: '',
    shopOrder: '',
    shopCancellation: '',
    payoutPercent: '',
    payoutCap: '',
    airtimePercent: '',
    dataPercent: '',
    cablePercent: '',
    electricityPercent: '',
    freeConsumer: '',
    freeBusiness: '',
    whatsappReminder: '',
  });
  const [staged, setStaged] = useState({
    transferMarkup: 0,
    intraBankMarkup: 0,
    fxMarkup: 0,
    cardNgn: 0,
    cardUsd: 0,
    cardPhysical: 0,
    cardBusiness: 0,
    shopOrder: 0,
    shopCancellation: 0,
    payoutPercent: 0,
    payoutCap: 0,
    airtimePercent: 0,
    dataPercent: 0,
    cablePercent: 0,
    electricityPercent: 0,
    freeConsumer: 0,
    freeBusiness: 0,
    whatsappReminder: 0,
  });

  const canEdit = admin && (admin.role === 'super_admin' || admin.role === 'finance');

  const getTransferMarkup = () =>
    fees.find((f) => f.feeKey === 'transfer_markup')?.amount ?? 0;
  // Missing row means ₦0: transfers to PayIQ accounts are free until set.
  const getIntraBankMarkup = () =>
    fees.find((f) => f.feeKey === 'transfer_markup_intrabank')?.amount ?? 0;
  const getShopOrderFee = () =>
    fees.find((f) => f.feeKey === 'shop_order_transaction_fee')?.amount ?? 0;
  const getShopCancellationFee = () =>
    fees.find((f) => f.feeKey === 'shop_order_cancellation_fee')?.amount ?? 0;
  const getPayoutFeePercent = () =>
    fees.find((f) => f.feeKey === 'vendor_payout_fee_percent')?.amount ?? 2;
  const getPayoutFeeCap = () =>
    fees.find((f) => f.feeKey === 'vendor_payout_fee_cap')?.amount ?? 500;
  const getWhatsappReminderFee = () =>
    fees.find((f) => f.feeKey === 'whatsapp_reminder_fee')?.amount ?? 25;
  const getAirtimeDiscountPercent = () =>
    fees.find((f) => f.feeKey === 'airtime_discount_percent')?.amount ?? 0;
  const feeOf = (key: string) => fees.find((f) => f.feeKey === key)?.amount ?? 0;
  const getDataCommission = () => feeOf('vas_commission_data_percent');
  const getCableCommission = () => feeOf('vas_commission_cable_percent');
  const getElectricityCommission = () => feeOf('vas_commission_electricity_percent');
  const getFreeConsumer = () => feeOf('free_transfers_monthly');
  const getCommissionEnabled = () => feeOf('vas_commission_enabled') >= 1;
  const getDisco = (key: string) => feeOf(`vas_commission_electricity_${key}`);
  const [discoDraft, setDiscoDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    setDiscoDraft(
      Object.fromEntries(DISCOS.map((d) => [d.key, String(getDisco(d.key))])),
    );
    // Re-read whenever the saved rates change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fees]);
  const discoDirty = DISCOS.some(
    (d) => Number(discoDraft[d.key]) !== Number(getDisco(d.key)),
  );
  const getFreeBusiness = () => feeOf('business_free_transfers_monthly');
  const getAirtimePassDiscount = () =>
    (fees.find((f) => f.feeKey === 'airtime_pass_discount_to_user')?.amount ??
      0) >= 1;

  const STAGED_RESET = {
    transferMarkup: 0,
    intraBankMarkup: 0,
    fxMarkup: 0,
    cardNgn: 0,
    cardUsd: 0,
    cardPhysical: 0,
    cardBusiness: 0,
    shopOrder: 0,
    shopCancellation: 0,
    payoutPercent: 0,
    payoutCap: 0,
    airtimePercent: 0,
    dataPercent: 0,
    cablePercent: 0,
    electricityPercent: 0,
    freeConsumer: 0,
    freeBusiness: 0,
    whatsappReminder: 0,
  };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [feesData, fxData] = await Promise.all([
        configApi.getFeeConfigs(),
        configApi.getFxConfig(),
      ]);
      setFees(feesData);
      setFxConfig(fxData as unknown as FxConfig);
    } catch (err) {
      toast.error(err, 'Failed to load fee configuration.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const startEdit = (field: EditingField, currentVal: string) => {
    setEditingField(field);
    setEditValues((s) => ({ ...s, [field as string]: currentVal }));
  };

  const stageAndConfirm = (field: EditingField, type: PendingType) => {
    const val = parseFloat(editValues[field as keyof typeof editValues]);
    if (isNaN(val) || val < 0) return;
    if (
      (field === 'cardNgn' || field === 'cardUsd' || field === 'cardPhysical') &&
      val === 0
    ) {
      toast.error('This card fee must be more than 0.');
      return;
    }
    if ((field === 'freeConsumer' || field === 'freeBusiness') && !Number.isInteger(val)) {
      toast.error('Free transfers must be a whole number.');
      return;
    }
    if (
      (field === 'airtimePercent' ||
        field === 'dataPercent' ||
        field === 'cablePercent' ||
        field === 'electricityPercent') &&
      val > 10
    ) {
      toast.error('A commission above 10% is not something Safe Haven pays.');
      return;
    }
    setStaged((s) => ({ ...s, [field as string]: val }));
    setEditingField(null);
    setPendingType(type);
  };

  const executeAction = async () => {
    if (!pendingType) return;
    try {
      setActionLoading(true);
      if (pendingType === 'transfer') {
        await configApi.updateTransferFee(staged.transferMarkup);
        toast.success('Transfer fee updated.');
      } else if (pendingType === 'intraBankTransfer') {
        await configApi.updateIntraBankTransferFee(staged.intraBankMarkup);
        toast.success(
          staged.intraBankMarkup > 0
            ? 'PayIQ-to-PayIQ transfer markup updated.'
            : 'PayIQ-to-PayIQ transfers are free.',
        );
      } else if (pendingType === 'fx') {
        await configApi.updateFxConfig(
            fxConfig?.baseNgnUsdRate ?? 0,
            staged.fxMarkup,
        );
        toast.success('FX markup updated.');
      } else if (pendingType === 'cardNgn') {
        await configApi.updateCardFees({ cardCreationFeeNgn: staged.cardNgn });
        toast.success('NGN card fee updated.');
      } else if (pendingType === 'cardUsd') {
        await configApi.updateCardFees({ cardCreationFeeUsdCents: staged.cardUsd });
        toast.success('USD card fee updated.');
      } else if (pendingType === 'cardPhysical') {
        await configApi.updateCardFees({
          cardPhysicalCreationFeeNgn: staged.cardPhysical,
        });
        toast.success('Physical card fee updated.');
      } else if (pendingType === 'cardBusiness') {
        await configApi.updateCardFees({
          businessCardCreationFeeNgn: staged.cardBusiness,
        });
        toast.success(
          staged.cardBusiness > 0
            ? 'Business card fee updated.'
            : 'Business cards are free.',
        );
      } else if (pendingType === 'shopOrder') {
        await configApi.updateShopFees({ orderTransactionFee: staged.shopOrder });
        toast.success('Order transaction fee updated.');
      } else if (pendingType === 'shopCancellation') {
        await configApi.updateShopFees({ cancellationFee: staged.shopCancellation });
        toast.success('Cancellation fee updated.');
      } else if (pendingType === 'payoutPercent') {
        await configApi.updatePayoutFee({ percent: staged.payoutPercent });
        toast.success('Payout fee percent updated.');
      } else if (pendingType === 'payoutCap') {
        await configApi.updatePayoutFee({ cap: staged.payoutCap });
        toast.success('Payout fee cap updated.');
      } else if (pendingType === 'whatsappReminder') {
        await configApi.updateWhatsappReminderFee(staged.whatsappReminder);
        toast.success('WhatsApp reminder fee updated.');
      } else if (pendingType === 'airtimePercent') {
        await configApi.updateVasCommission({ airtime: staged.airtimePercent });
        toast.success('Airtime commission updated.');
      } else if (pendingType === 'dataPercent') {
        await configApi.updateVasCommission({ data: staged.dataPercent });
        toast.success('Data commission updated.');
      } else if (pendingType === 'cablePercent') {
        await configApi.updateVasCommission({ cable: staged.cablePercent });
        toast.success('Cable TV commission updated.');
      } else if (pendingType === 'electricityPercent') {
        await configApi.updateVasCommission({ electricity: staged.electricityPercent });
        toast.success('Electricity commission updated.');
      } else if (pendingType === 'discos') {
        await configApi.updateVasCommission({
          discos: Object.fromEntries(
            DISCOS.map((d) => [d.key, Number(discoDraft[d.key])]),
          ),
        });
        toast.success('Electricity rates updated.');
      } else if (pendingType === 'enableCommission') {
        await configApi.updateVasCommission({ enabled: true });
        toast.success('Commission collection is on.');
      } else if (pendingType === 'freeConsumer') {
        await configApi.updateFreeTransfers({ consumerMonthly: staged.freeConsumer });
        toast.success('Free transfers for people updated.');
      } else if (pendingType === 'freeBusiness') {
        await configApi.updateFreeTransfers({ businessMonthly: staged.freeBusiness });
        toast.success('Free transfers for businesses updated.');
      }
      await fetchAll();
      setPendingType(null);
      setStaged(STAGED_RESET);
    } catch (err) {
      toast.error(err, 'Failed to update configuration.');
    } finally {
      setActionLoading(false);
    }
  };

  const getDialogMessage = () => {
    if (pendingType === 'transfer')
      return `The interbank transfer markup will be set to ₦${staged.transferMarkup}. It applies to transfers to other banks only, on top of Safe Haven's commission, from the next transfer.`;
    if (pendingType === 'intraBankTransfer')
      return staged.intraBankMarkup > 0
        ? `Transfers to PayIQ and other Safe Haven accounts will cost ₦${staged.intraBankMarkup} plus 7.5% VAT (₦${(Math.round(staged.intraBankMarkup * 1.075 * 100) / 100).toLocaleString('en-NG')} in all), from the next transfer. Safe Haven charges nothing for these, so this is PayIQ's whole charge. Savings moves stay free.`
        : 'Transfers to PayIQ and other Safe Haven accounts will be free, from the next transfer.';
    if (pendingType === 'cardNgn')
      return `Creating a Naira virtual card will cost ₦${staged.cardNgn.toLocaleString('en-NG')}, from the next card.`;
    if (pendingType === 'cardUsd')
      return `Creating a USD virtual card will cost ${staged.cardUsd} cents, from the next card.`;
    if (pendingType === 'cardPhysical')
      return `Ordering a physical card will cost ₦${staged.cardPhysical.toLocaleString('en-NG')}, from the next order.`;
    if (pendingType === 'cardBusiness')
      return staged.cardBusiness > 0
        ? `A business issuing a virtual card will pay ₦${staged.cardBusiness.toLocaleString('en-NG')} from its main account, from the next card.`
        : 'Businesses will issue virtual cards for free, from the next card.';
    if (pendingType === 'shopOrder')
      return `Order transaction fee will be set to ₦${staged.shopOrder} (0 = free). Shown on the checkout summary immediately.`;
    if (pendingType === 'shopCancellation')
      return `Cancellation fee will be set to ₦${staged.shopCancellation} (0 = free). Withheld from buyer refunds on cancel.`;
    if (pendingType === 'payoutPercent')
      return `Vendor payout fee will be set to ${staged.payoutPercent}% of each withdrawal (capped). Applies to new payout requests.`;
    if (pendingType === 'payoutCap')
      return `Vendor payout fee cap will be set to ₦${staged.payoutCap}. The percent fee never exceeds this.`;
    if (pendingType === 'whatsappReminder')
      return `Automatic WhatsApp reminders past a plan's allowance will cost ₦${staged.whatsappReminder} each (0 = free). Applies to reminders sent after this, never to ones already charged.`;
    const commission = (kind: string, pct: number) =>
      `${kind} commission will be set to ${pct}% of face value. On each new purchase, PayIQ moves this share from the payer's account to settlement, so the payer pays face value. It must match what Safe Haven pays: a higher rate charges the payer more than face value.`;
    if (pendingType === 'airtimePercent') return commission('Airtime', staged.airtimePercent);
    if (pendingType === 'dataPercent') return commission('Data', staged.dataPercent);
    if (pendingType === 'cablePercent') return commission('Cable TV', staged.cablePercent);
    if (pendingType === 'electricityPercent')
      return commission('Electricity', staged.electricityPercent);
    if (pendingType === 'discos')
      return `Electricity commission will be set per company as entered. Each must match Safe Haven's rate for that company: a higher one charges the payer more than face value.`;
    if (pendingType === 'enableCommission')
      return `PayIQ will start taking the commission from the payer's account after each airtime, data, cable and electricity purchase, at the rates below. Only do this once you've confirmed Safe Haven debits the payer less than face value (e.g. buy ₦100 of airtime and check that ₦97 left the account). If Safe Haven pays the commission to PayIQ separately instead, this would charge customers twice.`;
    if (pendingType === 'freeConsumer')
      return `Each person will get ${staged.freeConsumer} free transfer${staged.freeConsumer === 1 ? '' : 's'} a month (0 = off). PayIQ pays Safe Haven's fee on them; stamp duty still applies.`;
    if (pendingType === 'freeBusiness')
      return `Each business will get ${staged.freeBusiness} free transfer${staged.freeBusiness === 1 ? '' : 's'} a month (0 = off). Single transfers only, not bulk or payroll. PayIQ pays Safe Haven's fee on them; stamp duty still applies.`;
    return '';
  };

  // Off takes effect at once; on goes through the confirm dialog above.
  const toggleCommission = async () => {
    if (!getCommissionEnabled()) {
      setPendingType('enableCommission');
      return;
    }
    try {
      setActionLoading(true);
      await configApi.updateVasCommission({ enabled: false });
      await fetchAll();
      toast.success('Commission collection is off.');
    } catch (err) {
      toast.error(err, 'Failed to update commission collection.');
    } finally {
      setActionLoading(false);
    }
  };

  const saveDiscos = () => {
    const bad = DISCOS.find((d) => {
      const v = Number(discoDraft[d.key]);
      return !Number.isFinite(v) || v < 0 || v > 10;
    });
    if (bad) {
      toast.error(`${bad.label}: enter a rate between 0 and 10%.`);
      return;
    }
    setPendingType('discos');
  };

  // Toggle "pass discount to user" immediately (no staged-confirm needed).
  const togglePassDiscount = async () => {
    try {
      setActionLoading(true);
      await configApi.updateAirtimeDiscount({
        passDiscountToUser: !getAirtimePassDiscount(),
      });
      await fetchAll();
      toast.success('Airtime discount setting updated.');
    } catch (err) {
      toast.error(err, 'Failed to update airtime discount setting.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card padding={0}>
          <SectionHeader
            title="Transfer Fees"
            description="Flat markup charged per successful transfer, plus 7.5% VAT"
          />
          <FieldRow
            index={0}
            label="Interbank Transfer Markup"
            description="Added to each transfer to another bank, on top of Safe Haven's commission"
            displayValue={`₦${Number(getTransferMarkup()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
            field="transferMarkup"
            editingField={editingField}
            editValue={editValues.transferMarkup}
            onEditChange={(val) => setEditValues((s) => ({ ...s, transferMarkup: val }))}
            onEdit={() => startEdit('transferMarkup', String(getTransferMarkup()))}
            onSave={() => stageAndConfirm('transferMarkup', 'transfer')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <FieldRow
            index={1}
            label="PayIQ-to-PayIQ Transfer Markup"
            description="Transfers to PayIQ and other Safe Haven accounts. Safe Haven charges nothing for these, so ₦0 keeps them free. Never applied to savings moves."
            displayValue={
              Number(getIntraBankMarkup()) > 0
                ? `₦${Number(getIntraBankMarkup()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`
                : 'Free'
            }
            field="intraBankMarkup"
            editingField={editingField}
            editValue={editValues.intraBankMarkup}
            onEditChange={(val) => setEditValues((s) => ({ ...s, intraBankMarkup: val }))}
            onEdit={() => startEdit('intraBankMarkup', String(getIntraBankMarkup()))}
            onSave={() => stageAndConfirm('intraBankMarkup', 'intraBankTransfer')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
        </Card>

        {fxConfig && (
          <Card padding={0}>
            <SectionHeader
              title="FX Configuration"
              description="Markup percentage applied on top of the live provider rate for USD transactions"
            />
            <FieldRow
              index={0}
              label="FX Markup Percent"
              description="Percentage markup added to the live exchange rate"
              displayValue={`${fxConfig.markupPercent}%`}
              field="fxMarkup"
              editingField={editingField}
              editValue={editValues.fxMarkup}
              onEditChange={(val) => setEditValues((s) => ({ ...s, fxMarkup: val }))}
              onEdit={() => startEdit('fxMarkup', String(fxConfig.markupPercent))}
              onSave={() => stageAndConfirm('fxMarkup', 'fx')}
              onCancel={() => setEditingField(null)}
              canEdit={!!canEdit}
            />
          </Card>
        )}

        {fxConfig && (
          <Card padding={0}>
            <SectionHeader
              title="Card Creation Fees"
              description="One-time fees for creating or ordering a card"
            />
            <FieldRow
              index={0}
              label="NGN Card Creation Fee"
              description="Fee to create a Naira virtual card"
              displayValue={`₦${Number(fxConfig.cardCreationFeeNgn).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
              field="cardNgn"
              editingField={editingField}
              editValue={editValues.cardNgn}
              onEditChange={(val) => setEditValues((s) => ({ ...s, cardNgn: val }))}
              onEdit={() => startEdit('cardNgn', String(fxConfig.cardCreationFeeNgn))}
              onSave={() => stageAndConfirm('cardNgn', 'cardNgn')}
              onCancel={() => setEditingField(null)}
              canEdit={!!canEdit}
            />
            <FieldRow
              index={1}
              label="USD Card Creation Fee"
              description="Fee to create a USD virtual card (in cents)"
              displayValue={`${fxConfig.cardCreationFeeUsdCents} cents`}
              field="cardUsd"
              editingField={editingField}
              editValue={editValues.cardUsd}
              onEditChange={(val) => setEditValues((s) => ({ ...s, cardUsd: val }))}
              onEdit={() => startEdit('cardUsd', String(fxConfig.cardCreationFeeUsdCents))}
              onSave={() => stageAndConfirm('cardUsd', 'cardUsd')}
              onCancel={() => setEditingField(null)}
              canEdit={!!canEdit}
            />
            <FieldRow
              index={2}
              label="Physical Card Fee"
              description="Fee to order a physical Naira card, covering the card and delivery"
              displayValue={`₦${Number(fxConfig.cardPhysicalCreationFeeNgn ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
              field="cardPhysical"
              editingField={editingField}
              editValue={editValues.cardPhysical}
              onEditChange={(val) => setEditValues((s) => ({ ...s, cardPhysical: val }))}
              onEdit={() =>
                startEdit('cardPhysical', String(fxConfig.cardPhysicalCreationFeeNgn ?? 0))
              }
              onSave={() => stageAndConfirm('cardPhysical', 'cardPhysical')}
              onCancel={() => setEditingField(null)}
              canEdit={!!canEdit}
            />
            {fxConfig.businessCardCreationFeeNgn !== undefined && (
              <FieldRow
                index={3}
                label="Business Card Fee"
                description="Fee a business pays to issue a virtual card, from its main account. 0 = free"
                displayValue={
                  Number(fxConfig.businessCardCreationFeeNgn) > 0
                    ? `₦${Number(fxConfig.businessCardCreationFeeNgn).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`
                    : 'Free'
                }
                field="cardBusiness"
                editingField={editingField}
                editValue={editValues.cardBusiness}
                onEditChange={(val) => setEditValues((s) => ({ ...s, cardBusiness: val }))}
                onEdit={() =>
                  startEdit('cardBusiness', String(fxConfig.businessCardCreationFeeNgn ?? 0))
                }
                onSave={() => stageAndConfirm('cardBusiness', 'cardBusiness')}
                onCancel={() => setEditingField(null)}
                canEdit={!!canEdit}
              />
            )}
          </Card>
        )}

        <Card padding={0}>
          <SectionHeader
            title="Messaging"
            description="What a business pays for messages PayIQ sends on its behalf. Chases sent from their own WhatsApp are free and are never counted."
          />
          <FieldRow
            index={0}
            label="WhatsApp Reminder"
            description="Per automatic reminder past a plan's monthly allowance. The allowance itself is per-plan — edit it in Plans › feature matrix."
            displayValue={`₦${Number(getWhatsappReminderFee()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
            field="whatsappReminder"
            editingField={editingField}
            editValue={editValues.whatsappReminder}
            onEditChange={(val) =>
              setEditValues((s) => ({ ...s, whatsappReminder: val }))
            }
            onEdit={() =>
              startEdit('whatsappReminder', String(getWhatsappReminderFee()))
            }
            onSave={() => stageAndConfirm('whatsappReminder', 'whatsappReminder')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
        </Card>

        <Card padding={0}>
          <SectionHeader
            title="Shop Fees"
            description="Order and cancellation fees for the marketplace. Set to 0 to show buyers “Free”."
          />
          <FieldRow
            index={0}
            label="Order Transaction Fee"
            description="Flat fee added to each checkout, shown on the order summary"
            displayValue={`₦${Number(getShopOrderFee()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
            field="shopOrder"
            editingField={editingField}
            editValue={editValues.shopOrder}
            onEditChange={(val) => setEditValues((s) => ({ ...s, shopOrder: val }))}
            onEdit={() => startEdit('shopOrder', String(getShopOrderFee()))}
            onSave={() => stageAndConfirm('shopOrder', 'shopOrder')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <FieldRow
            index={1}
            label="Cancellation Fee"
            description="Withheld from a buyer's refund when they cancel an order"
            displayValue={`₦${Number(getShopCancellationFee()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
            field="shopCancellation"
            editingField={editingField}
            editValue={editValues.shopCancellation}
            onEditChange={(val) => setEditValues((s) => ({ ...s, shopCancellation: val }))}
            onEdit={() => startEdit('shopCancellation', String(getShopCancellationFee()))}
            onSave={() => stageAndConfirm('shopCancellation', 'shopCancellation')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
        </Card>

        <Card padding={0}>
          <SectionHeader
            title="Vendor Payout Fee"
            description="Charged on each vendor withdrawal — a percent of the amount, capped."
          />
          <FieldRow
            index={0}
            label="Payout Fee Percent"
            description="Percent of each withdrawal withheld as the payout fee"
            displayValue={`${Number(getPayoutFeePercent())}%`}
            field="payoutPercent"
            editingField={editingField}
            editValue={editValues.payoutPercent}
            onEditChange={(val) => setEditValues((s) => ({ ...s, payoutPercent: val }))}
            onEdit={() => startEdit('payoutPercent', String(getPayoutFeePercent()))}
            onSave={() => stageAndConfirm('payoutPercent', 'payoutPercent')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <FieldRow
            index={1}
            label="Payout Fee Cap"
            description="Maximum payout fee in ₦ regardless of percent"
            displayValue={`₦${Number(getPayoutFeeCap()).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
            field="payoutCap"
            editingField={editingField}
            editValue={editValues.payoutCap}
            onEditChange={(val) => setEditValues((s) => ({ ...s, payoutCap: val }))}
            onEdit={() => startEdit('payoutCap', String(getPayoutFeeCap()))}
            onSave={() => stageAndConfirm('payoutCap', 'payoutCap')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
        </Card>

        <Card padding={0}>
          <SectionHeader
            title="Free Transfers"
            description="Transfers each month with no fee: PayIQ pays Safe Haven's fee, and the ₦50 stamp duty still applies. Shown as a counter on the send-money confirmation. 0 = off."
          />
          <FieldRow
            index={0}
            label="People, per month"
            description="Free transfers each person gets every month"
            displayValue={`${Number(getFreeConsumer())}`}
            field="freeConsumer"
            editingField={editingField}
            editValue={editValues.freeConsumer}
            onEditChange={(val) => setEditValues((s) => ({ ...s, freeConsumer: val }))}
            onEdit={() => startEdit('freeConsumer', String(getFreeConsumer()))}
            onSave={() => stageAndConfirm('freeConsumer', 'freeConsumer')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <FieldRow
            index={1}
            label="Businesses, per month"
            description="Free single transfers each business gets every month (not bulk or payroll)"
            displayValue={`${Number(getFreeBusiness())}`}
            field="freeBusiness"
            editingField={editingField}
            editValue={editValues.freeBusiness}
            onEditChange={(val) => setEditValues((s) => ({ ...s, freeBusiness: val }))}
            onEdit={() => startEdit('freeBusiness', String(getFreeBusiness()))}
            onSave={() => stageAndConfirm('freeBusiness', 'freeBusiness')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
        </Card>

        <CreditFeeCover fees={fees} canEdit={!!canEdit} onSaved={fetchAll} />

        <Card padding={0}>
          <SectionHeader
            title="Airtime & Bill Commission"
            description="Safe Haven pays a commission by charging the payer less than face value. PayIQ charges face value and moves the commission to settlement. Rates are from Safe Haven's pricing sheet (max 10%)."
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(min(100%, 220px), 1fr) auto auto',
              gap: 16,
              padding: '16px 20px',
              alignItems: 'center',
              background: getCommissionEnabled() ? 'var(--neutral-100)' : 'var(--neutral-200)',
              borderBottom: '1px solid var(--neutral-300)',
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)' }}>
                Collect commission
              </div>
              <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 }}>
                Off: nothing is taken, whatever the rates say. Turn on once you've confirmed Safe Haven debits the payer less than face value.
              </div>
            </div>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: getCommissionEnabled()
                  ? 'var(--green-700, #15803d)'
                  : 'var(--neutral-500)',
                fontFamily: 'monospace',
              }}
            >
              {getCommissionEnabled() ? 'ON' : 'OFF'}
            </span>
            {canEdit && (
              <Button
                size="sm"
                variant="secondary"
                onClick={toggleCommission}
                disabled={actionLoading}
              >
                {getCommissionEnabled() ? 'Turn off' : 'Turn on'}
              </Button>
            )}
          </div>
          <FieldRow
            index={0}
            label="Airtime"
            description="Percent of face value"
            displayValue={`${Number(getAirtimeDiscountPercent())}%`}
            field="airtimePercent"
            editingField={editingField}
            editValue={editValues.airtimePercent}
            onEditChange={(val) => setEditValues((s) => ({ ...s, airtimePercent: val }))}
            onEdit={() => startEdit('airtimePercent', String(getAirtimeDiscountPercent()))}
            onSave={() => stageAndConfirm('airtimePercent', 'airtimePercent')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <FieldRow
            index={1}
            label="Data"
            description="Percent of face value"
            displayValue={`${Number(getDataCommission())}%`}
            field="dataPercent"
            editingField={editingField}
            editValue={editValues.dataPercent}
            onEditChange={(val) => setEditValues((s) => ({ ...s, dataPercent: val }))}
            onEdit={() => startEdit('dataPercent', String(getDataCommission()))}
            onSave={() => stageAndConfirm('dataPercent', 'dataPercent')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <FieldRow
            index={2}
            label="Cable TV"
            description="Percent of face value"
            displayValue={`${Number(getCableCommission())}%`}
            field="cablePercent"
            editingField={editingField}
            editValue={editValues.cablePercent}
            onEditChange={(val) => setEditValues((s) => ({ ...s, cablePercent: val }))}
            onEdit={() => startEdit('cablePercent', String(getCableCommission()))}
            onSave={() => stageAndConfirm('cablePercent', 'cablePercent')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <FieldRow
            index={3}
            label="Electricity"
            description="Percent of face value"
            displayValue={`${Number(getElectricityCommission())}%`}
            field="electricityPercent"
            editingField={editingField}
            editValue={editValues.electricityPercent}
            onEditChange={(val) => setEditValues((s) => ({ ...s, electricityPercent: val }))}
            onEdit={() => startEdit('electricityPercent', String(getElectricityCommission()))}
            onSave={() => stageAndConfirm('electricityPercent', 'electricityPercent')}
            onCancel={() => setEditingField(null)}
            canEdit={!!canEdit}
          />
          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--neutral-300)' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--neutral-800)' }}>
              Electricity, by company
            </div>
            <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2, marginBottom: 12 }}>
              Safe Haven's rate differs by company. A biller that can't be matched to one uses the Electricity rate above, never more than 0.8%.
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 10,
              }}
            >
              {DISCOS.map((d) => (
                <label
                  key={d.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 13,
                    color: 'var(--neutral-700)',
                  }}
                >
                  {d.label}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      min={0}
                      max={10}
                      step={0.1}
                      value={discoDraft[d.key] ?? ''}
                      disabled={!canEdit || actionLoading}
                      onChange={(e) =>
                        setDiscoDraft((s) => ({ ...s, [d.key]: e.target.value }))
                      }
                      style={{
                        width: 64,
                        height: 30,
                        padding: '0 8px',
                        fontSize: 13,
                        border: '1px solid var(--neutral-300)',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--neutral-100)',
                        color: 'var(--neutral-800)',
                      }}
                    />
                    %
                  </span>
                </label>
              ))}
            </div>
            {canEdit ? (
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={saveDiscos} disabled={!discoDirty || actionLoading}>
                  Save electricity rates
                </Button>
              </div>
            ) : null}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(min(100%, 220px), 1fr) auto auto',
              gap: 16,
              padding: '16px 20px',
              alignItems: 'center',
              background: 'var(--neutral-200)',
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--neutral-800)' }}>
                Pass airtime discount to customer
              </div>
              <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 }}>
                On: customer pays the discounted airtime price. Off: PayIQ keeps the airtime commission and the customer pays face value. Bills always keep it.
              </div>
            </div>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: getAirtimePassDiscount()
                  ? 'var(--green-700, #15803d)'
                  : 'var(--neutral-500)',
                fontFamily: 'monospace',
              }}
            >
              {getAirtimePassDiscount() ? 'ON' : 'OFF'}
            </span>
            {canEdit && (
              <Button
                size="sm"
                variant="secondary"
                onClick={togglePassDiscount}
                disabled={actionLoading}
              >
                {getAirtimePassDiscount() ? 'Turn off' : 'Turn on'}
              </Button>
            )}
          </div>
        </Card>
      </div>

      <ConfirmDialog
        isOpen={pendingType !== null}
        title="Update fee configuration?"
        message={getDialogMessage()}
        confirmLabel="Update"
        variant="primary"
        loading={actionLoading}
        onConfirm={executeAction}
        onCancel={() => {
          setPendingType(null);
          setStaged(STAGED_RESET);
        }}
      />
    </>
  );
};
